import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface CustomerMapping {
  integrationId: string
  externalId: string
  externalName?: string
  syncStatus: string
  lastSyncAt?: string
}

interface SetupCustomer {
  id: string
  name: string
  domain?: string
  status: string
  primaryContact?: string
  primaryEmail?: string
  phone?: string
  notes?: string
  totalDevices: number
  totalLicenses: number
  totalUsers: number
  monthlyRevenue: number
  mappings: CustomerMapping[]
}

interface Integration {
  id: string
  displayName: string
  description?: string
  isEnabled: boolean
}

interface ExternalCustomer {
  externalId: string
  name: string
  domain?: string
  isMapped: boolean
}

export default function SetupCustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  const [customer, setCustomer] = useState<SetupCustomer | null>(null)
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  const [editForm, setEditForm] = useState({
    name: '',
    domain: '',
    status: 'Active',
    primaryContact: '',
    primaryEmail: '',
    phone: '',
    notes: '',
  })

  // Linking state
  const [linkingIntegration, setLinkingIntegration] = useState<string | null>(null)
  const [externalCustomers, setExternalCustomers] = useState<ExternalCustomer[]>([])
  const [selectedExternalId, setSelectedExternalId] = useState<string>('')

  useEffect(() => {
    async function fetchData() {
      if (!customerId) return

      try {
        setLoading(true)
        setError(null)
        const token = await getAccessToken()

        const [customerData, integrationsData] = await Promise.all([
          api.getSetupCustomer(token, customerId),
          api.getSetupIntegrations(token),
        ])

        setCustomer(customerData.customer)
        setIntegrations(integrationsData.integrations || [])
        setEditForm({
          name: customerData.customer.name || '',
          domain: customerData.customer.domain || '',
          status: customerData.customer.status || 'Active',
          primaryContact: customerData.customer.primaryContact || '',
          primaryEmail: customerData.customer.primaryEmail || '',
          phone: customerData.customer.phone || '',
          notes: customerData.customer.notes || '',
        })
      } catch (err) {
        console.error('Failed to fetch customer:', err)
        setError('Failed to load customer details.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [customerId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!customerId) return

    try {
      setSaving(true)
      const token = await getAccessToken()
      const updated = await api.updateSetupCustomer(token, customerId, editForm)
      setCustomer({ ...customer!, ...updated.customer })
    } catch (err) {
      console.error('Failed to save customer:', err)
      setError('Failed to save customer.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!customerId) return
    if (!confirm('Are you sure you want to delete this customer? This cannot be undone.')) return

    try {
      setDeleting(true)
      const token = await getAccessToken()
      await api.deleteSetupCustomer(token, customerId)
      navigate('/setup/customers')
    } catch (err) {
      console.error('Failed to delete customer:', err)
      setError('Failed to delete customer.')
      setDeleting(false)
    }
  }

  const handleStartLinking = async (integrationId: string) => {
    try {
      const token = await getAccessToken()
      const data = await api.getExternalCustomers(token, integrationId)
      setExternalCustomers(data.customers || [])
      setLinkingIntegration(integrationId)
      setSelectedExternalId('')
    } catch (err) {
      console.error('Failed to fetch external customers:', err)
      setError('Failed to load customers from integration.')
    }
  }

  const handleLink = async () => {
    if (!customerId || !linkingIntegration || !selectedExternalId) return

    try {
      const token = await getAccessToken()
      const selectedCustomer = externalCustomers.find((c) => c.externalId === selectedExternalId)
      await api.createCustomerMapping(token, customerId, {
        integrationId: linkingIntegration,
        externalId: selectedExternalId,
        externalName: selectedCustomer?.name,
      })

      // Refresh customer data
      const customerData = await api.getSetupCustomer(token, customerId)
      setCustomer(customerData.customer)
      setLinkingIntegration(null)
    } catch (err) {
      console.error('Failed to link customer:', err)
      setError('Failed to link customer.')
    }
  }

  const handleUnlink = async (integrationId: string) => {
    if (!customerId) return
    if (!confirm('Are you sure you want to unlink this integration?')) return

    try {
      const token = await getAccessToken()
      await api.deleteCustomerMapping(token, customerId, integrationId)

      // Refresh customer data
      const customerData = await api.getSetupCustomer(token, customerId)
      setCustomer(customerData.customer)
    } catch (err) {
      console.error('Failed to unlink:', err)
      setError('Failed to unlink integration.')
    }
  }

  const handleSyncLicenses = async () => {
    if (!customerId) return

    try {
      setSyncing(true)
      setSyncMessage(null)
      setError(null)
      const token = await getAccessToken()

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/setup/customers/${customerId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (!response.ok) {
        // Show both error and details if available
        const errorMsg = data.details
          ? `${data.error}: ${data.details}`
          : (data.error || 'Sync failed')
        throw new Error(errorMsg)
      }

      setSyncMessage(`Synced ${data.totalLicenses} licenses successfully`)

      // Refresh customer data
      const customerData = await api.getSetupCustomer(token, customerId)
      setCustomer(customerData.customer)
    } catch (err) {
      console.error('Failed to sync:', err)
      setError(err instanceof Error ? err.message : 'Failed to sync licenses.')
    } finally {
      setSyncing(false)
    }
  }

  const getMapping = (integrationId: string) => {
    return customer?.mappings.find((m) => m.integrationId === integrationId)
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading customer...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="px-4 sm:px-0">
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">Customer not found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      {/* Breadcrumb */}
      <nav className="mb-4">
        <Link to="/setup/customers" className="text-primary-600 hover:text-primary-900">
          ← Back to Customers
        </Link>
      </nav>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {syncMessage && (
        <div className="mb-4 rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-800">{syncMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Details Form */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Domain</label>
                <input
                  type="text"
                  value={editForm.domain}
                  onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Prospect">Prospect</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Primary Contact</label>
                <input
                  type="text"
                  value={editForm.primaryContact}
                  onChange={(e) => setEditForm({ ...editForm, primaryContact: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={editForm.primaryEmail}
                  onChange={(e) => setEditForm({ ...editForm, primaryEmail: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Customer'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editForm.name.trim()}
                className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        {/* Integration Mappings */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Integration Mappings</h2>
            <p className="text-sm text-gray-500 mb-4">
              Link this customer to their records in external systems.
            </p>

            <div className="space-y-4">
              {integrations.map((integration) => {
                const mapping = getMapping(integration.id)
                const isLinking = linkingIntegration === integration.id

                return (
                  <div
                    key={integration.id}
                    className={`border rounded-lg p-4 ${
                      mapping ? 'border-green-200 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{integration.displayName}</span>
                      {!integration.isEnabled && (
                        <span className="text-xs text-gray-400">Not configured</span>
                      )}
                    </div>

                    {mapping ? (
                      <div>
                        <p className="text-sm text-gray-600">
                          Linked to: <span className="font-medium">{mapping.externalName || mapping.externalId}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Status: {mapping.syncStatus}
                          {mapping.lastSyncAt && ` • Last sync: ${new Date(mapping.lastSyncAt).toLocaleDateString()}`}
                        </p>
                        <div className="mt-2 flex gap-3">
                          {integration.id === 'partner-center' && (
                            <button
                              onClick={handleSyncLicenses}
                              disabled={syncing}
                              className="text-sm text-primary-600 hover:text-primary-800 disabled:opacity-50"
                            >
                              {syncing ? 'Syncing...' : 'Sync Licenses'}
                            </button>
                          )}
                          <button
                            onClick={() => handleUnlink(integration.id)}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Unlink
                          </button>
                        </div>
                      </div>
                    ) : integration.isEnabled ? (
                      isLinking ? (
                        <div className="mt-2">
                          <select
                            value={selectedExternalId}
                            onChange={(e) => setSelectedExternalId(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          >
                            <option value="">Select customer...</option>
                            {externalCustomers
                              .filter((c) => !c.isMapped)
                              .map((c) => (
                                <option key={c.externalId} value={c.externalId}>
                                  {c.name} {c.domain && `(${c.domain})`}
                                </option>
                              ))}
                          </select>
                          <div className="mt-2 flex gap-2">
                            <button
                              onClick={handleLink}
                              disabled={!selectedExternalId}
                              className="text-sm text-primary-600 hover:text-primary-800 disabled:opacity-50"
                            >
                              Link
                            </button>
                            <button
                              onClick={() => setLinkingIntegration(null)}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleStartLinking(integration.id)}
                          className="mt-2 text-sm text-primary-600 hover:text-primary-800"
                        >
                          + Link to {integration.displayName}
                        </button>
                      )
                    ) : (
                      <p className="text-sm text-gray-400 mt-1">
                        Enable this integration in Settings to link customers.
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Stats Card */}
          <div className="bg-white shadow rounded-lg p-6 mt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Summary</h3>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Licenses</dt>
                <dd className="text-sm font-medium text-gray-900">{customer.totalLicenses}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Users</dt>
                <dd className="text-sm font-medium text-gray-900">{customer.totalUsers}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Devices</dt>
                <dd className="text-sm font-medium text-gray-900">{customer.totalDevices}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
